import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { Panel } from "@/components/Panel/Panel";
import { sbaLoanAmortizationPaymentsAtom } from "@/financeModels/owasco/amortization";
import { useableFreeCashFlowMolecule } from "@/financeModels/owasco/projections";
import {
  InvestorTypeId,
  allInvestorTypesAtom,
  basicReturnMetricsAtoms,
  coReturnMetricsAtoms,
  investorDistributionTableListAtom,
} from "@/financeModels/owasco/returnMetrics";
import { formatMoney } from "@/utils/moneyFormating";
import {
  Box,
  Divider,
  Group,
  ScrollArea,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useTimeout } from "@mantine/hooks";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { CapTableEquityValue } from "./CapTableEquityValue";
import { CapTableInvestor } from "./CapTableInvestor";
import { CapTableOwnership } from "./CapTableOwnership";
import { CashOnReturnTable } from "./CashOnReturnTable";
import { CumulativePrincipalRepaymentTable } from "./CumulativePrincipalRepaymentTable";
import { IndividualInvestorReturnsTable } from "./IndividualInvestorReturnsTable";
import { InvestorDistributionsTable } from "./InvestorDistributionsTable";
import { IndividualInvestorReturnInput } from "./InvidualInvestorReturnInput";
import { ReturnMetricsTable } from "./ReturnMetrics";

export const PanelROI = () => {
  // This panel requires some calcs from the useableFreeCashFlowMolecule
  // otherwise it loads slow, loading them first before showing the panel
  // it will use caches instead of recalculating
  const [show, setShow] = useState(false);

  useTimeout(
    () => {
      setShow(true);
    },
    50,
    {
      autoInvoke: true,
    },
  );

  const tables = useAtomValue(investorDistributionTableListAtom);
  const investorTypes = useAtomValue(allInvestorTypesAtom);
  const availableInvestorTypes = investorTypes.filter((i) => i.isVisible);
  const investorSelectOptions = availableInvestorTypes.map((i) => ({
    label: (tables.find((t) => t.id === i.id)?.title || i.title).replace(
      "All Investors",
      "All Stakeholders",
    ),
    value: i.id,
  }));

  const [selectedInvestorTypeId, setSelectedInvestorTypeId] =
    useState<InvestorTypeId>(availableInvestorTypes[0]?.id ?? "all");

  const selectedInvestorType =
    availableInvestorTypes.find((i) => i.id === selectedInvestorTypeId) ||
    availableInvestorTypes[0];

  const selectedTable =
    tables.find((t) => t.id === selectedInvestorType?.id) || tables[0];

  // update if options disappear
  useEffect(() => {
    if (
      selectedInvestorTypeId &&
      !availableInvestorTypes.find((i) => i.id === selectedInvestorTypeId)
    ) {
      setSelectedInvestorTypeId(availableInvestorTypes[0]?.id ?? "");
    }
  }, [availableInvestorTypes, selectedInvestorTypeId]);

  return (
    <>
      <ROIFIX />
      <ScrollArea w="100%" offsetScrollbars flex={1} h={"calc(100% - 20px)"}>
        {show && (
          <Stack
            gap={"xs"}
            flex={1}
            h="100%"
            style={{ overflow: "hidden" }}
            pb={"40vh"}
          >
            <LabelExplainer
              label={
                <Text size="lg" fw={"bold"} my={"md"} lh={1}>
                  Cap Table
                </Text>
              }
              explanation={
                <Doc path="@/docs/pages/model/roi/cap-table-header.mdx" />
              }
            />

            <Group wrap="nowrap" align="stretch">
              <CapTableInvestor />
              <CapTableEquityValue />
              <CapTableOwnership />
            </Group>

            <LabelExplainer
              label={
                <Text size="lg" fw={"bold"} my={"md"} lh={1}>
                  Return Metrics
                </Text>
              }
              explanation={
                <Doc path="@/docs/pages/model/roi/return-metrics-header.mdx" />
              }
            />

            <ReturnMetricsTable
              headers={{
                title: {
                  label: "Summary - Searcher & Investors",
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-summary-searchers-investors.mdx",
                },
                companyOwnership: {
                  label: (
                    <>
                      Company <br /> Ownership
                    </>
                  ),
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-company-ownership.mdx",
                  w: 100,
                },
                totalDistributions: {
                  label: (
                    <>
                      Total <br />
                      Distributions
                    </>
                  ),
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-total-distributions.mdx",
                },

                saleProceeds: {
                  label: (
                    <>
                      Sale <br />
                      Proceeds
                    </>
                  ),
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-sale-proceeds.mdx",
                },
                totalReturn: {
                  label: (
                    <>
                      Total <br />
                      Return
                    </>
                  ),
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-total-return.mdx",
                },
                irr: {
                  label: "IRR",
                  docPath: "@/docs/pages/model/roi/return-metrics-irr.mdx",
                },
                moic: {
                  label: "MOIC",
                  docPath: "@/docs/pages/model/roi/return-metrics-MOIC.mdx",
                },
              }}
              rows={coReturnMetricsAtoms}
            />
            <ReturnMetricsTable
              headers={{
                title: {
                  label: "Breakdown - By Investor Type",
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-breakdown-by-investor-type.mdx",
                },
                companyOwnership: {
                  label: (
                    <>
                      Company <br /> Ownership
                    </>
                  ),
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-breakdown-company-ownership.mdx",
                  w: 100,
                },
                totalDistributions: {
                  label: (
                    <>
                      Total <br />
                      Distributions
                    </>
                  ),
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-breakdown-total-distributions.mdx",
                },

                saleProceeds: {
                  label: (
                    <>
                      Sale <br />
                      Proceeds
                    </>
                  ),
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-breakdown-sale-proceeds.mdx",
                },
                totalReturn: {
                  label: (
                    <>
                      Total <br />
                      Return
                    </>
                  ),
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-breakdown-total-return.mdx",
                },
                irr: {
                  label: "IRR",
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-breakdown-IRR.mdx",
                },
                moic: {
                  label: "MOIC",
                  docPath:
                    "@/docs/pages/model/roi/return-metrics-breakdown-MOIC.mdx",
                },
              }}
              rows={basicReturnMetricsAtoms}
            />

            <Stack>
              <Group align="center" h={80}>
                <LabelExplainer
                  label={
                    <Text size="lg" fw={"bold"} lh={1}>
                      Distributions
                    </Text>
                  }
                  explanation={
                    <Doc path="@/docs/pages/model/roi/distributions-header-title.mdx" />
                  }
                />

                <Divider orientation="vertical" color="gray.4" />
                <Select
                  withCheckIcon={false}
                  size="sm"
                  label={
                    <LabelExplainer
                      label={"Investor Type:"}
                      explanation={
                        <Doc path="@/docs/pages/model/roi/distributions-investor-type.mdx" />
                      }
                    />
                  }
                  data={investorSelectOptions}
                  value={selectedInvestorType?.id}
                  onChange={(val) => {
                    setSelectedInvestorTypeId(
                      (val as InvestorTypeId) || availableInvestorTypes[0]?.id,
                    );
                  }}
                  styles={{
                    dropdown: { zIndex: 100 },
                  }}
                />
                {selectedInvestorType?.id !== "all" && (
                  <>
                    <Divider orientation="vertical" color="gray.4" />
                    <Text fz="sm">
                      Equity Class : <br />
                      <b>
                        {selectedInvestorType?.equityType === "common"
                          ? "Common Equity"
                          : "Preferred & Common Equity"}
                      </b>
                    </Text>

                    <Divider orientation="vertical" color="gray.4" />
                    <Text fz="sm">
                      Equity Step-Up: {selectedInvestorType?.stepUpElection}{" "}
                      <br />
                      <b>
                        {selectedInvestorType?.stepUpElection === false ||
                        !selectedInvestorType?.stepUpApplies ||
                        selectedInvestorType?.equityType === "common"
                          ? "None"
                          : Number(selectedInvestorType?.multiplier) + "x"}
                      </b>
                    </Text>

                    <Divider orientation="vertical" color="gray.4" />
                    <Text fz="sm">
                      Invested Capital: <br />
                      <b>
                        {selectedInvestorType?.investedCapital
                          ? formatMoney(
                              Number(selectedInvestorType?.investedCapital) ||
                                0,
                            )
                          : "None"}
                      </b>
                    </Text>
                  </>
                )}
              </Group>

              <Group wrap="nowrap" align="stretch">
                <Panel p={0}>
                  <InvestorDistributionsTable table={selectedTable!} />
                </Panel>
                <Panel p={0}>
                  <CumulativePrincipalRepaymentTable
                    selectedInvestorTypeId={selectedInvestorTypeId}
                  />
                </Panel>
                <Panel p={0}>
                  <CashOnReturnTable
                    selectedInvestorTypeId={selectedInvestorTypeId}
                  />
                </Panel>
                <Stack gap={0} pos={"relative"}>
                  <Panel p={0}>
                    <IndividualInvestorReturnsTable
                      selectedInvestorTypeId={selectedInvestorTypeId}
                    />
                  </Panel>
                  <Box pos={"absolute"} top={-100} right={0}>
                    <IndividualInvestorReturnInput />
                  </Box>
                </Stack>
              </Group>
            </Stack>
          </Stack>
        )}
      </ScrollArea>
    </>
  );
};

const ROIFIX = () => {
  useAtomValue(sbaLoanAmortizationPaymentsAtom);
  useAtomValue(useableFreeCashFlowMolecule);
  return null;
};
